import { TFunction } from "i18next";
import { useLookupValuesSelector } from "../store/selectors/masterSelector";
import { LOOKUP_TYPE, LOOKUP_VALUE } from "./constant";

/**
 * Lookup values (statuses, discount types, etc.) come from the backend via
 * `lookupId`/`lookupValue`, not from frontend strings. Dozens of call sites
 * compare `item.lookupValue` against a literal English string to drive logic
 * (e.g. `lookupValue === "Percentage"`), so that field must never be mutated
 * in place. This translates only for rendering, keyed by the globally-unique
 * `lookupId`, falling back to the raw backend value for any id not yet mapped.
 */
export const getLookupDisplayValue = (item: any, t: TFunction): string =>
  t(String(item?.lookupId), { ns: "lookup", defaultValue: item?.lookupValue });

/**
 * The system-seeded default customer group's name ("Default Customer Group")
 * is fixed app copy, unlike merchant-created group names which are genuine
 * user data and must stay untranslated. Keyed off `isDefault` rather than a
 * string match so a merchant renaming their own group never collides with it.
 */
export const getCustomerGroupDisplayName = (group: any, t: TFunction): string =>
  group?.isDefault
    ? t("common.values.defaultGroupFullName", {
        ns: "customers",
        defaultValue: group?.customerGroupName,
      })
    : group?.customerGroupName;

/**
 * Country/State names come from the backend's fixed reference lists
 * (Country/Get, State/Get) -- not merchant-entered text -- so unlike a
 * vendor's or location's own name, they're safe to translate. Keyed by the
 * raw backend name (there's no stable shared code for states), falling back
 * to that raw name for any country/state not yet in the `common:geo` table.
 */
export const getCountryDisplayName = (countryName: string | undefined, t: TFunction): string =>
  t(`geo.countries.${countryName}`, { ns: "common", defaultValue: countryName });

export const getStateDisplayName = (stateName: string | undefined, t: TFunction): string =>
  t(`geo.states.${stateName}`, { ns: "common", defaultValue: stateName });

/**
 * Currency/Timezone names come from the backend's fixed reference lists
 * (Currency/Get, TimeZone/Get), same as country/state -- safe to translate.
 * The timezone's UTC offset (e.g. "(UTC-05:00)") is intentionally left out of
 * translation since it's a numeric, locale-neutral format, not a display name.
 */
export const getCurrencyDisplayName = (currencyName: string | undefined, t: TFunction): string =>
  t(`currencies.${currencyName}`, { ns: "common", defaultValue: currencyName });

export const getTimezoneDisplayName = (timezoneName: string | undefined, t: TFunction): string =>
  t(`timezones.${timezoneName}`, { ns: "common", defaultValue: timezoneName });

/**
 * Fallback for report/export rows that carry only a flattened group-name
 * string with no id or `isDefault` flag to cross-reference (e.g. loyalty
 * activity/report CSV exports). Matches the exact seeded default name, since
 * no reliable id is available in these payloads.
 */
export const getCustomerGroupDisplayNameByRawName = (name: string | undefined | null, t: TFunction): string =>
  name === "Default Customer Group"
    ? t("common.values.defaultGroupFullName", { ns: "customers", defaultValue: name })
    : (name ?? "");

const ORDER_TYPE_KEYS: Record<string, string> = {
  "Online": "online",
  "Offline": "offline",
  "Void": "void",
};

/**
 * Order type has no id/LOOKUP_TYPE anywhere in the frontend (unlike payment
 * method names, which are merchant-editable free text -- see
 * getPaymentMethodDisplayName). Known values are translated by exact match;
 * this is NOT guaranteed to be the full set (the field is untyped/passed
 * through as-is from the API), so anything else falls back to the raw value
 * rather than risk mistranslating a future/unseen backend value.
 */
export const getOrderTypeDisplayName = (orderType: string | undefined | null, t: TFunction): string => {
  const key = orderType ? ORDER_TYPE_KEYS[orderType] : undefined;
  return key ? t(`common.values.orderType.${key}`, { ns: "sales", defaultValue: orderType }) : (orderType ?? "");
};

const PAYMENT_METHOD_NAME_KEYS: Record<string, string> = {
  "Cash": "cash",
  "Card": "card",
  "Check": "check",
  "Store Credit": "storeCredit",
  "Gift Card": "giftCard",
  "EBT SNAP": "ebtSnap",
  "EBT Cash": "ebtCash",
};

/**
 * Payment method names come from merchant-configurable Settings > Payment
 * Methods (free text), so only the seeded default names are safe to
 * translate -- a merchant-renamed method is left untouched, same principle
 * as getCustomerGroupDisplayName's isDefault check.
 */
export const getPaymentMethodDisplayName = (name: string | undefined | null, t: TFunction): string => {
  const key = name ? PAYMENT_METHOD_NAME_KEYS[name] : undefined;
  return key ? t(`common.values.paymentMethods.${key}`, { ns: "sales", defaultValue: name }) : (name ?? "");
};

const DISCOUNT_MODE_NAME_TO_LOOKUP_ID: Record<string, number> = {
  "Special Pricing": 134,
  "Mix & Match Pricing": 135,
  "Buy X Get Y": 136,
  "Order Discount": 137,
  "Shipping Discount": 138,
};

/**
 * Sale By Discounts report rows carry only the raw DISCOUNT_MODE lookup text
 * (`discountType`), not the `lookupId` needed for getLookupDisplayValue --
 * same id-less-report-row gap as getOrderTypeDisplayName/
 * getPaymentMethodDisplayName. Reverse-maps the known names back to their
 * lookupId so this reuses the already-translated `lookup` namespace instead
 * of duplicating these strings into a new one. "N/A" (row has no discount)
 * isn't a DISCOUNT_MODE value at all, so it's translated separately via
 * `common.values.notApplicable`.
 */
export const getDiscountTypeDisplayName = (discountType: string | undefined | null, t: TFunction): string => {
  if (!discountType) return "";
  if (discountType === "N/A") return t("values.notApplicable", { ns: "common", defaultValue: discountType });
  const lookupId = DISCOUNT_MODE_NAME_TO_LOOKUP_ID[discountType];
  return lookupId ? getLookupDisplayValue({ lookupId }, t) : discountType;
};

const REFUND_STATUS_KEYS: Record<string, string> = {
  "Full Refund": "refundStatusValues.fullRefund",
  "Refunded": "refundStatusValues.refunded",
  "Partial Refund": "refundStatusValues.partialRefund",
  "Partially Refunded": "refundStatusValues.partiallyRefunded",
  "No Refund": "noRefund",
  "Order Returned": "refundStatusValues.orderReturned",
};

/**
 * Sales Report's refundStatus is a raw API string with no id/LOOKUP_TYPE
 * behind this specific badge (LOOKUP_TYPE.REFUND_STATUS exists and backs the
 * report's filter dropdown, but the results-table row only carries the plain
 * string). Falls back to the raw value for anything outside the known set --
 * this includes the API sending the literal string "No Refund" itself (as
 * opposed to an empty/falsy value, which callers handle separately via
 * t("salesReport.noRefund")).
 */
export const getRefundStatusDisplayName = (status: string | undefined | null, t: TFunction): string => {
  const key = status ? REFUND_STATUS_KEYS[status] : undefined;
  return key ? t(`salesReport.${key}`, { ns: "reports", defaultValue: status }) : (status ?? "");
};

const INVENTORY_MOVEMENT_TYPE_KEYS: Record<string, string> = {
  "Stock Return Receive": "stockReturnReceive",
  "Order Received": "orderReceived",
  "Transfer": "transfer",
  "Return Sale": "returnSale",
  "Sales": "sales",
};

/**
 * Inventory Movement Report's `inventoryType` is an untyped, free-form string
 * from the API with no enumerated set anywhere in the frontend (no filter
 * dropdown, no LOOKUP_TYPE) -- only the values seen in practice are mapped
 * here; extend as new raw values surface.
 */
export const getInventoryMovementTypeDisplayName = (type: string | undefined | null, t: TFunction): string => {
  const key = type ? INVENTORY_MOVEMENT_TYPE_KEYS[type] : undefined;
  return key ? t(`inventoryMovementReport.typeValues.${key}`, { ns: "reports", defaultValue: type }) : (type ?? "");
};

const STOCK_DIRECTION_KEYS: Record<string, string> = {
  "In": "in",
  "Out": "out",
};

export const getStockDirectionDisplayName = (direction: string | undefined | null, t: TFunction): string => {
  const key = direction ? STOCK_DIRECTION_KEYS[direction] : undefined;
  return key ? t(`inventoryMovementReport.stockDirection.${key}`, { ns: "reports", defaultValue: direction }) : (direction ?? "");
};

/**
 * "Unassigned" is the backend's placeholder for "no vendor assigned to this
 * product", seen raw in report rows (e.g. Inventory Movement Report's vendor
 * column). Reuses the same wording already established for this exact
 * concept in the `inventory` namespace (src/pages/Inventory/StockControl's
 * NewPO.tsx), just under `reports` so any report can reuse it without a
 * cross-namespace lookup.
 */
export const getReportVendorDisplayName = (vendor: string | undefined | null, t: TFunction): string =>
  vendor === "Unassigned" ? t("common.values.unassigned", { ns: "reports", defaultValue: vendor }) : (vendor ?? "");

const ADJUSTMENT_REASON_KEYS: Record<string, string> = {
  "Manually Stock Update": "manuallyStockUpdate",
  "PO Receive": "poReceive",
};

/**
 * Stock Adjustment Report's `adjustmentReason` is an untyped, free-form
 * string with no enumerated set anywhere in the frontend (no lookupId on the
 * row, no matching LOOKUP_TYPE) -- only the values seen in practice are
 * mapped here; extend as new raw values surface.
 */
export const getAdjustmentReasonDisplayName = (reason: string | undefined | null, t: TFunction): string => {
  const key = reason ? ADJUSTMENT_REASON_KEYS[reason] : undefined;
  return key ? t(`adjustmentsReport.reasonValues.${key}`, { ns: "reports", defaultValue: reason }) : (reason ?? "");
};

const SYSTEM_DEFAULT_ROLE_KEYS: Record<string, string> = {
  "OwnerAdmin": "ownerAdmin",
  "Manager": "manager",
  "Cashier": "cashier",
};

/**
 * The 3 seeded system-default roles (OwnerAdmin/Manager/Cashier) are safe to
 * translate; merchant-created custom roles (free text) are not. Use this
 * variant when the full role object is available, keyed off the same
 * isOwnerAdmin/isManagerAdmin/isCashier flags used elsewhere in the app
 * (e.g. UserRoles.tsx's "System Default" badge) rather than a name match,
 * since a merchant could theoretically rename their own role to collide.
 */
export const getRoleDisplayName = (role: any, t: TFunction): string => {
  const isSystemDefault = !!(role?.isOwnerAdmin || role?.isManagerAdmin || role?.isCashier);
  const key = isSystemDefault && role?.roleName ? SYSTEM_DEFAULT_ROLE_KEYS[role.roleName] : undefined;
  return key ? t(`users.userRoles.values.${key}`, { ns: "setting", defaultValue: role?.roleName }) : (role?.roleName ?? "");
};

/**
 * Fallback for contexts that only carry a flattened role-name string with no
 * isOwnerAdmin/isManagerAdmin/isCashier flags to key off (report rows, CSV
 * exports, dropdown options built from a user record). Matches by exact
 * name, same trade-off as getCustomerGroupDisplayNameByRawName.
 */
export const getRoleDisplayNameByRawName = (roleName: string | undefined | null, t: TFunction): string => {
  const key = roleName ? SYSTEM_DEFAULT_ROLE_KEYS[roleName] : undefined;
  return key ? t(`users.userRoles.values.${key}`, { ns: "setting", defaultValue: roleName }) : (roleName ?? "");
};

const TAX_NAME_KEYS: Record<string, string> = {
  "State": "state",
  "County": "county",
  "District": "district",
  "Country": "country",
  "City": "city",
};

/**
 * Tax Report's breakdown rows (and the category column's "State: X, County: Y"
 * sub-labels) carry a raw jurisdiction-level name ("State"/"County"/etc.) that
 * gets interpolated into common.taxNamed ("{{name}} Tax") -- translating the
 * whole interpolated string wouldn't work since t() doesn't recurse into an
 * interpolated variable, so the raw name itself must be translated first.
 */
export const getTaxNameDisplay = (name: string | undefined | null, t: TFunction): string => {
  const key = name ? TAX_NAME_KEYS[name] : undefined;
  return key ? t(`common.values.taxNames.${key}`, { ns: "reports", defaultValue: name }) : (name ?? "");
};

const EXEMPTION_STATUS_KEYS: Record<string, string> = {
  "Taxable": "taxable",
  "Non-Taxable": "nonTaxable",
  "Exempt": "exempt",
};

export const getExemptionStatusDisplayName = (status: string | undefined | null, t: TFunction): string => {
  const key = status ? EXEMPTION_STATUS_KEYS[status] : undefined;
  return key ? t(`taxReport.values.exemptionStatus.${key}`, { ns: "reports", defaultValue: status }) : (status ?? "");
};

const PRODUCT_TYPE_KEYS: Record<string, string> = {
  "Standard": "standard",
  "Variant": "variant",
  "Bundle": "bundle",
};

/**
 * Reuses the translations already established for these same 3 values in
 * src/pages/Product/DisplayOrder/_components/ProductCard.tsx, rather than
 * duplicating them under a report-specific key.
 */
export const getProductTypeDisplayName = (type: string | undefined | null, t: TFunction): string => {
  const key = type ? PRODUCT_TYPE_KEYS[type] : undefined;
  return key ? t(`displayOrder.productTypes.${key}`, { ns: "productManagement", defaultValue: type }) : (type ?? "");
};

export const useLookup = () => {
    const lookups = useLookupValuesSelector();

    const getLookup = (type: any) => {
        return lookups?.filter((l: any) => l.lookupType == type) || [];
    };

    return {
        getProductType: () => getLookup(LOOKUP_TYPE.PRODUCT_TYPE)
        .filter((item: any) => item.lookupValue !== "Physical Gift Card"),
        getProductStatus: () => getLookup(LOOKUP_TYPE.PRODUCT_STATUS),
        getReturnPeriod: () => getLookup(LOOKUP_TYPE.RETURN_PERIOD),
        getAccuracy: () => getLookup(LOOKUP_TYPE.ACCURACY),
        getMarkupMargin: () => getLookup(LOOKUP_TYPE.MARKUP_MARGIN),
        getInventoryStatus: () => getLookup(LOOKUP_TYPE.INVENTORY_STATUS).filter((item: any) => item.lookupId !== LOOKUP_VALUE.INVENTORY_STATUS_OVER_STOCK),
        getDimensionUnits: () => getLookup(LOOKUP_TYPE.DIMENSION_UNITS),
        getAddressType: () => getLookup(LOOKUP_TYPE.ADDRESS_TYPE),
        getContactType: () => getLookup(LOOKUP_TYPE.CONTACT_TYPE),
        getLeadTimeBy: () => getLookup(LOOKUP_TYPE.LEAD_TIME_BY),
        getPOReceivingMethod: () => getLookup(LOOKUP_TYPE.PO_RECEIVING_METHOD),
        getPerBoxType: () => getLookup(LOOKUP_TYPE.PER_BOX_TYPE),
        getGender: () => getLookup(LOOKUP_TYPE.GENDER),
        getCustomerType: () => getLookup(LOOKUP_TYPE.CUSTOMER_TYPE),
        getDiscountType: () => getLookup(LOOKUP_TYPE.DISCOUNT_TYPE),
        getDiscountMinPurchaseAmount: () => getLookup(LOOKUP_TYPE.DISCOUNT_MIN_PURCHASE_AMOUNT),
        getDiscountMaxAllowed: () => getLookup(LOOKUP_TYPE.DISCOUNT_MAX_ALLOWED),
        getDiscountUsageLimit: () => getLookup(LOOKUP_TYPE.DISCOUNT_USAGE_LIMIT),
        getDiscountCombination: () => getLookup(LOOKUP_TYPE.DISCOUNT_COMBINATION),
        getDiscountMode: () => getLookup(LOOKUP_TYPE.DISCOUNT_MODE),
        getDiscountProductRole: () => getLookup(LOOKUP_TYPE.DISCOUNT_PRODUCT_ROLE),
        getCustomerGroupType: () => getLookup(LOOKUP_TYPE.CUSTOMER_GROUP_TYPE),
        getDiscountTargetCustomer: () => getLookup(LOOKUP_TYPE.DISCOUNT_TARGET_CUSTOMER),
        getCustomerGroupScheduleFrequency: () => getLookup(LOOKUP_TYPE.CUSTOMER_GROUP_SCHEDULE_FREQUENCY),
        getCustomerGroupChannels: () => getLookup(LOOKUP_TYPE.CUSTOMER_GROUP_CHANNELS),
        getLoyaltyType: () => getLookup(LOOKUP_TYPE.LOYALTY_TYPE),
        getLoyaltyDiscountType: () => getLookup(LOOKUP_TYPE.LOYALTY_DISCOUNT_TYPE),
        getRewardType: () => getLookup(LOOKUP_TYPE.REWARD_TYPE),
        getProgramCategory: () => getLookup(LOOKUP_TYPE.PROGRAM_CATEGORY),
        // getCommissionType: () => getLookup(LOOKUP_TYPE.COMMISSION_TYPE),
        getScheduleCountType: () => getLookup(LOOKUP_TYPE.SCHEDULE_COUNT_TYPE),
        getReturnStatus: () => getLookup(LOOKUP_TYPE.RETURN_STATUS),
        getReturnMethod: () => getLookup(LOOKUP_TYPE.RETURN_METHOD),
        getPOStatus: () => getLookup(LOOKUP_TYPE.PO_STATUS),
        getReceiveOrderStatus: () => getLookup(LOOKUP_TYPE.RECEIVE_ORDER_STATUS),
        getReceiveOrderReasons: () => getLookup(LOOKUP_TYPE.RECEIVE_ORDER_REASONS),
        getLoyaltyExpiry: () => getLookup(LOOKUP_TYPE.LOYALTY_EXPIRY),
        getSetupReturnRefundMethod: () => getLookup(LOOKUP_TYPE.SETUP_RETURN_REFUND_METHOD),
        getSetupReturnShippingCost: () => getLookup(LOOKUP_TYPE.SETUP_RETURN_SHIPPING_COST),
        getSetupReturnRestockingFee: () => getLookup(LOOKUP_TYPE.SETUP_RETURN_RESTOCKING_FEE),
        getActionType: () => getLookup(LOOKUP_TYPE.ACTION_TYPE),
        getPunchType: () => getLookup(LOOKUP_TYPE.PUNCH_TYPE),
        getRoleApplicationType: () => getLookup(LOOKUP_TYPE.ROLE_APPLICATION_TYPE),
        getGiftCardType: () => getLookup(LOOKUP_TYPE.GIFT_CARD_TYPE),
        getActivationKeyType: () => getLookup(LOOKUP_TYPE.ACTIVATION_KEY_TYPE),
        getOrderStatus: () => getLookup(LOOKUP_TYPE.ORDER_STATUS),
        getLocationType: () => getLookup(LOOKUP_TYPE.LOCATION_TYPE),
        getLocationStatus: () => getLookup(LOOKUP_TYPE.LOCATION_STATUS),
        getRestockReason: () => getLookup(LOOKUP_TYPE.RESTOCK_REASON),
        getMeasuredBy: () => getLookup(LOOKUP_TYPE.MEASURED_BY),
        getLotteryTicketType: () => getLookup(LOOKUP_TYPE.LOTTERY_TICKET_TYPE),
        getTransferStatus: () => getLookup(LOOKUP_TYPE.TRANSFER_STATUS),
        getVoidReason: () => getLookup(LOOKUP_TYPE.VOID_REASON),
        getFrameType: () => getLookup(LOOKUP_TYPE.FRAME_TYPE),
        getReceiptPaymentType: () => getLookup(LOOKUP_TYPE.RECEIPT_PAYMENT_TYPE),
        getPriceType: () => getLookup(LOOKUP_TYPE.PRICE_TYPE),
        
        getRestrictedScheduleType: () => getLookup(LOOKUP_TYPE.RESTRICTED_SCHEDULE_TYPE),
        getRestrictedProductType: () => getLookup(LOOKUP_TYPE.RESTRICTED_PRODUCT_TYPE),
        getRefundStatus: () => getLookup(LOOKUP_TYPE.REFUND_STATUS),
    };
};
